import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import SEO from '../components/SEO';
import AnimatedLogo from '../components/AnimatedLogo';
import {
  Paper, Text, Title, Badge, Group, Stack, Progress, SimpleGrid,
  Box, Anchor, Loader, Flex, Button, Select, SegmentedControl, Tooltip,
} from '@mantine/core';
import {
  ChartBar, Megaphone, Lightbulb, Pulse, ArrowRight,
  CursorClick, Target, Lightning, CircleNotch, CurrencyDollar,
  TrendUp, TrendDown, Buildings,
} from '@phosphor-icons/react';
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip as RTooltip, Legend,
} from 'recharts';
import ExportMenu from '../components/ExportMenu';
import StickyHeader from '../components/StickyHeader';
import api from '../utils/api';
import { formatMoney, currencySymbol } from '../utils/money';

function GA4Widget() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/ads/ga4/report/')
      .then(res => { if (res.data.status === 'success') setData(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Flex align="center" gap="xs" justify="center" py="sm">
        <CircleNotch size={14} className="animate-spin" style={{ color: 'var(--mantine-color-gray-5)' }} />
        <Text size="xs" c="dimmed">{t('dashboard.ga4.loading')}</Text>
      </Flex>
    </Paper>
  );

  if (!data) return null;

  const c = data.current || {};
  const ch = data.changes || {};

  const metrics = [
    { key: 'sessions', label: t('dashboard.ga4.metrics.sessions'), fmt: v => (v || 0).toLocaleString('pl-PL') },
    { key: 'users', label: t('dashboard.ga4.metrics.users'), fmt: v => (v || 0).toLocaleString('pl-PL') },
    { key: 'conversions', label: t('dashboard.ga4.metrics.conversions'), fmt: v => v || 0 },
    { key: 'bounce_rate', label: t('dashboard.ga4.metrics.bounceRate'), fmt: v => `${(v || 0).toFixed(1)}%`, invert: true },
    { key: 'avg_session_duration', label: t('dashboard.ga4.metrics.avgSessionDuration'), fmt: v => { const s = v || 0; return s >= 60 ? `${Math.floor(s/60)}m ${Math.round(s%60)}s` : `${Math.round(s)}s`; } },
    { key: 'pages_per_session', label: t('dashboard.ga4.metrics.pagesPerSession'), fmt: v => (v || 0).toFixed(1) },
  ];

  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Group justify="space-between" mb="sm">
        <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>Google Analytics 4</Text>
        <Text size="xs" c="dimmed">{t('dashboard.ga4.lastDays', { days: data.days })}</Text>
      </Group>
      <SimpleGrid cols={{ base: 3, md: 6 }} spacing="sm">
        {metrics.map(m => {
          const val = c[m.key];
          const change = ch[m.key];
          const isGood = m.invert ? change < 0 : change > 0;
          const isNeutral = change === 0;
          const noComp = change === null || change === undefined;
          return (
            <div key={m.key} className="text-center">
              <Text size="xs" c="dimmed" mb={2}>{m.label}</Text>
              <Text size="sm" fw={600} ff="monospace">{m.fmt(val)}</Text>
              {noComp ? (
                <Text size="xs" c="gray.4" mt={2}>-</Text>
              ) : !isNeutral ? (
                <Text size="xs" c={isGood ? 'green.6' : 'red.6'} fw={500} mt={2}>
                  {change > 0 ? <TrendUp size={10} weight="bold" style={{ display: 'inline', marginRight: 2 }} /> : <TrendDown size={10} weight="bold" style={{ display: 'inline', marginRight: 2 }} />}
                  {change > 0 ? '+' : ''}{change}%
                </Text>
              ) : (
                <Text size="xs" c="gray.4" mt={2}>0%</Text>
              )}
            </div>
          );
        })}
      </SimpleGrid>

      {/* Top sources */}
      {data.sources && data.sources.length > 0 && (
        <Box mt="md">
          <Text size="xs" fw={600} c="dimmed" mb="xs">{t('dashboard.ga4.trafficSources')}</Text>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="xs">
            {data.sources.slice(0, 8).map((s, i) => (
              <Paper key={i} p="xs" bg="gray.0" radius="sm">
                <Text size="xs" fw={500} truncate>{s.channel}</Text>
                <Text size="xs" c="dimmed">{t('dashboard.ga4.sessions', { count: s.sessions.toLocaleString('pl-PL') })}{s.conversions > 0 ? t('dashboard.ga4.conversionsInline', { count: s.conversions }) : ''}</Text>
              </Paper>
            ))}
          </SimpleGrid>
        </Box>
      )}
    </Paper>
  );
}

function EmptyCard({ icon, title, message, linkTo, linkLabel }) {
  return (
    <Paper p="lg" withBorder shadow="xs" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: 180 }}>
      <Box
        style={{
          width: 40, height: 40, borderRadius: 10,
          background: 'var(--mantine-color-gray-1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 12,
        }}
      >
        {icon}
      </Box>
      <Text size="sm" fw={500} c="dark">{title}</Text>
      <Text size="xs" c="dimmed" mt={4}>{message}</Text>
      {linkTo && (
        <Anchor component={Link} to={linkTo} size="xs" fw={500} mt="sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {linkLabel} <ArrowRight size={12} weight="bold" />
        </Anchor>
      )}
    </Paper>
  );
}

function StatCard({ label, value, secondaryValue, secondaryLabel, monthLabel, empty, icon, tooltip }) {
  const { t } = useTranslation();
  const secLabel = secondaryLabel ?? t('dashboard.stats.secondaryLabel');
  const monthLbl = monthLabel ?? t('dashboard.stats.monthLabel');
  const card = (
    <Paper p="md" withBorder shadow="xs" style={{ cursor: tooltip && !empty ? 'help' : undefined }}>
      <Group justify="space-between" align="flex-start">
        <Box>
          <Text size="xs" c="dimmed" tt="uppercase" fw={500} style={{ letterSpacing: '0.05em' }}>{label}</Text>
          {empty ? (
            <Text size="sm" c="gray.3" mt={8}>{t('dashboard.stats.noData')}</Text>
          ) : (
            <>
              <Text size="xl" fw={700} c="dark" mt={4} style={{ lineHeight: 1.1 }}>{value}</Text>
              <Text size="10px" c="dimmed" tt="uppercase" fw={500} style={{ letterSpacing: '0.04em' }}>{monthLbl}</Text>
              {secondaryValue !== undefined && (
                <Text size="xs" c="dimmed" mt={6}>{secLabel}: <Text span fw={600} c="gray.7">{secondaryValue}</Text></Text>
              )}
            </>
          )}
        </Box>
        {icon && (
          <Box
            style={{
              width: 36, height: 36, borderRadius: 8,
              background: 'var(--mantine-color-brand-0)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        )}
      </Group>
    </Paper>
  );
  if (tooltip && !empty) {
    return (
      <Tooltip label={tooltip} multiline w={300} withArrow position="bottom" color="dark" events={{ hover: true, focus: true, touch: true }}>
        {card}
      </Tooltip>
    );
  }
  return card;
}

const METRIC_OPTIONS = [
  { value: 'cost', labelKey: 'dashboard.chart.metrics.cost', color: '#1D4ED8', type: 'bar' },
  { value: 'clicks', labelKey: 'dashboard.chart.metrics.clicks', color: '#3b82f6', type: 'line' },
  { value: 'impressions', labelKey: 'dashboard.chart.metrics.impressions', color: '#8b5cf6', type: 'line' },
  { value: 'conversions', labelKey: 'dashboard.chart.metrics.conversions', color: '#10b981', type: 'line' },
  { value: 'cpc', labelKey: 'dashboard.chart.metrics.cpc', color: '#ef4444', type: 'line' },
  { value: 'budget_amount', labelKey: 'dashboard.chart.metrics.budget_amount', color: '#3b82f6', type: 'line' },
];

function PerformanceChart({ campaignId, isConnected, period, setPeriod, currency = 'PLN' }) {
  const { t } = useTranslation();
  const cur = currencySymbol(currency);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(['cost', 'clicks']);
  const days = period === 'month' ? new Date().getDate() : Number(period);

  useEffect(() => {
    setLoading(true);
    const params = `days=${days}${campaignId ? `&campaign_id=${campaignId}` : ''}`;
    api.get(`/ads/dashboard/chart/?${params}`)
      .then(res => {
        const raw = res.data.days || [];
        setChartData(raw.map(d => ({
          ...d,
          date: d.date.length > 7 ? d.date.slice(5) : d.date, // "2026-05-01" -> "05-01"
        })));
      })
      .catch(() => setChartData([]))
      .finally(() => setLoading(false));
  }, [days, campaignId]);

  const toggleMetric = (m) => {
    setMetrics(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m].slice(-3));
  };

  if (loading) return (
    <Paper p="md" withBorder shadow="xs" style={{ minHeight: 300 }}>
      <Flex align="center" justify="center" h={260}><Loader size="sm" color="brand" /></Flex>
    </Paper>
  );

  if (chartData.length === 0) return (
    <EmptyCard
      icon={<ChartBar size={20} weight="duotone" color="var(--mantine-color-gray-5)" />}
      title={t('dashboard.chart.emptyTitle')}
      message={isConnected
        ? t('dashboard.chart.emptyMessageConnected')
        : t('dashboard.chart.emptyMessageDisconnected')}
      linkTo={isConnected ? undefined : '/app/campaigns'}
      linkLabel={isConnected ? undefined : t('dashboard.chart.connectAccount')}
    />
  );

  const activeMetrics = METRIC_OPTIONS.filter(m => metrics.includes(m.value));

  return (
    <Paper p="md" withBorder shadow="xs">
      <Group justify="space-between" mb="sm">
        <Text size="sm" fw={600} c="dark">{t('dashboard.chart.title')}</Text>
        <Group gap="xs">
          <SegmentedControl
            value={period}
            onChange={setPeriod}
            data={[
              { value: '7', label: '7d' },
              { value: '14', label: '14d' },
              { value: '30', label: '30d' },
              { value: 'month', label: t('dashboard.chart.periodMonth') },
              { value: '90', label: '90d' },
            ]}
            size="xs"
          />
        </Group>
      </Group>

      <Group gap={4} mb="sm" wrap="wrap">
        {METRIC_OPTIONS.map(m => (
          <Button
            key={m.value}
            size="compact-xs"
            variant={metrics.includes(m.value) ? 'filled' : 'light'}
            color="gray"
            onClick={() => toggleMetric(m.value)}
            styles={{
              root: {
                backgroundColor: metrics.includes(m.value) ? m.color : undefined,
                borderColor: metrics.includes(m.value) ? m.color : undefined,
              },
            }}
          >
            {t(m.labelKey, { cur })}
          </Button>
        ))}
      </Group>

      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#ccc" />
          {activeMetrics.map((m, i) => (
            <YAxis
              key={m.value}
              yAxisId={m.value}
              orientation={i === 0 ? 'left' : 'right'}
              tick={{ fontSize: 10 }}
              stroke={m.color}
              width={45}
              hide={i > 1}
            />
          ))}
          <RTooltip
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #eee' }}
            formatter={(val, name) => {
              const m = METRIC_OPTIONS.find(o => o.value === name);
              return [typeof val === 'number' ? val.toLocaleString('pl-PL') : val, m ? t(m.labelKey, { cur }) : name];
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {activeMetrics.map(m => (
            m.type === 'bar' ? (
              <Bar key={m.value} dataKey={m.value} yAxisId={m.value} fill={m.color} radius={[3, 3, 0, 0]} barSize={16} name={t(m.labelKey, { cur })} opacity={0.8} />
            ) : (
              <Line key={m.value} dataKey={m.value} yAxisId={m.value} stroke={m.color} strokeWidth={2} dot={false} name={t(m.labelKey, { cur })} />
            )
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </Paper>
  );
}

function CampaignsList({ campaigns, selectedAccount, periodMetrics = {}, period = '30' }) {
  const { t } = useTranslation();
  const periodText = period === 'month'
    ? t('dashboard.periods.month')
    : t('dashboard.periods.days', { count: period });
  // Metrics for the selected period (falls back to the 30-day campaign values).
  const periodRow = (c) => {
    const m = periodMetrics?.[c.id];
    if (!m) return { cost: c.spend || 0, clicks: c.clicks || 0, impressions: c.impressions || 0, conversions: c.conversions || 0, ctr: c.ctr || 0 };
    const ctr = m.impressions > 0 ? +(m.clicks / m.impressions * 100).toFixed(1) : 0;
    return { cost: m.cost || 0, clicks: m.clicks || 0, impressions: m.impressions || 0, conversions: m.conversions || 0, ctr };
  };
  const isOtherAccount = (c) => (
    c.platform_account_id && selectedAccount && selectedAccount !== '__all__'
    && c.platform_account_id !== selectedAccount
  );
  const handleCampaignClick = async (e, c) => {
    if (!isOtherAccount(c)) return; // same account: let the Link navigate normally
    e.preventDefault();
    try {
      await api.post('/accounts/select-ad-account/', {
        platform: 'google_ads', account_id: c.platform_account_id,
      });
    } catch (_) { /* navigate anyway; scope just won't switch */ }
    // Full navigation so every panel re-fetches under the newly selected account.
    window.location.assign(`/app/campaigns?campaign=${c.id || ''}`);
  };

  if (!campaigns || campaigns.length === 0) {
    return (
      <EmptyCard
        icon={<Megaphone size={20} weight="duotone" color="var(--mantine-color-gray-5)" />}
        title={t('dashboard.campaigns.emptyTitle')}
        message={t('dashboard.campaigns.emptyMessage')}
        linkTo="/app/campaigns"
        linkLabel={t('dashboard.campaigns.manageCampaigns')}
      />
    );
  }


  return (
    <Paper p="md" withBorder shadow="xs">
      <Group justify="space-between" mb="sm">
        <Group gap={6} align="baseline">
          <Text size="sm" fw={600} c="dark">{t('dashboard.campaigns.title')}</Text>
          <Text size="xs" c="dimmed">· {periodText}</Text>
        </Group>
        <Anchor component={Link} to="/app/campaigns" size="xs">{t('dashboard.campaigns.all')}</Anchor>
      </Group>
      <Stack gap={4}>
        {campaigns.map((c, i) => { const r = periodRow(c); return (
          <Anchor
            key={i}
            component={Link}
            to={`/app/campaigns?campaign=${c.id || ''}`}
            onClick={(e) => handleCampaignClick(e, c)}
            underline="never"
            style={{ display: 'block', padding: '10px 10px', margin: '0 -10px', borderRadius: 8, transition: 'background 150ms' }}
            className="campaign-row"
          >
            <Group justify="space-between" wrap="nowrap" mb={4}>
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <Box style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: c.active ? 'var(--mantine-color-green-5)' : 'var(--mantine-color-yellow-5)' }} />
                <Text size="sm" c="dark" truncate fw={500}>{c.name}</Text>
                {isOtherAccount(c) && (
                  <Tooltip label={t('dashboard.campaigns.otherAccountTooltip', { account: c.account_name || c.platform_account_id })} withArrow>
                    <Badge size="xs" variant="light" color="gray" leftSection={<Buildings size={9} weight="bold" />} style={{ flexShrink: 0, textTransform: 'none', maxWidth: 160 }}>
                      {c.account_name || t('dashboard.campaigns.otherAccountBadge')}
                    </Badge>
                  </Tooltip>
                )}
              </Group>
              <Text size="sm" fw={600} c="dark" style={{ flexShrink: 0 }}>{formatMoney(r.cost, c.currency)}</Text>
            </Group>
            <Group gap="lg">
              <Text size="xs" c="dimmed">{t('dashboard.campaigns.clicks', { count: r.clicks.toLocaleString('pl-PL') })}</Text>
              <Text size="xs" c="dimmed">{t('dashboard.campaigns.impressions', { count: r.impressions.toLocaleString('pl-PL') })}</Text>
              <Text size="xs" c="dimmed">{t('dashboard.campaigns.conversions', { count: Math.round(r.conversions) })}</Text>
              <Text size="xs" c="dimmed">{t('dashboard.campaigns.ctr', { value: r.ctr })}</Text>
            </Group>
          </Anchor>
        ); })}
      </Stack>
    </Paper>
  );
}

function SuggestionsList({ suggestions, onApply, applyingIds }) {
  const { t } = useTranslation();
  const priorityColor = (p) => {
    if (p === 'high') return 'red';
    if (p === 'medium') return 'yellow';
    return 'gray';
  };

  if (!suggestions || suggestions.length === 0) {
    return (
      <EmptyCard
        icon={<Lightbulb size={20} weight="duotone" color="var(--mantine-color-gray-5)" />}
        title={t('dashboard.suggestions.emptyTitle')}
        message={t('dashboard.suggestions.emptyMessage')}
      />
    );
  }

  return (
    <Paper p="md" withBorder shadow="xs">
      <Text size="sm" fw={600} c="dark" mb="sm">{t('dashboard.suggestions.title')}</Text>
      <Stack gap="xs">
        {suggestions.map((item) => (
          <Group key={item.id} gap="sm" wrap="nowrap" align="center" className="campaign-row" style={{ padding: '8px 10px', margin: '0 -10px', borderRadius: 8, transition: 'background 150ms' }}>
            <Box style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: `var(--mantine-color-${priorityColor(item.priority)}-5)` }} />
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" c="gray.7">{item.title}</Text>
              <Text size="xs" c="dimmed">{item.campaign_name || item.campaign || ''}</Text>
            </Box>
            <Button
              size="compact-sm"
              variant="light"
              color="brand"
              onClick={() => onApply(item)}
              disabled={applyingIds.includes(item.id)}
              loading={applyingIds.includes(item.id)}
              loaderProps={{ type: 'dots' }}
              style={{ flexShrink: 0 }}
            >
              {t('dashboard.suggestions.apply')}
            </Button>
          </Group>
        ))}
      </Stack>
    </Paper>
  );
}

function ActivityTimeline({ items }) {
  const { t } = useTranslation();
  if (!items || items.length === 0) {
    return (
      <EmptyCard
        icon={<Pulse size={20} weight="duotone" color="var(--mantine-color-gray-5)" />}
        title={t('dashboard.activity.emptyTitle')}
        message={t('dashboard.activity.emptyMessage')}
      />
    );
  }

  return (
    <Paper p="md" withBorder shadow="xs">
      <Text size="sm" fw={600} c="dark" mb="sm">{t('dashboard.activity.title')}</Text>
      <Stack gap="xs">
        {items.map((item, i) => (
          <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
            <Box style={{ width: 5, height: 5, borderRadius: '50%', marginTop: 7, flexShrink: 0, background: 'var(--mantine-color-gray-4)' }} />
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Text size="sm" c="gray.7">{item.text}</Text>
              <Text size="xs" c="dimmed">{item.time}</Text>
            </Box>
          </Group>
        ))}
      </Stack>
    </Paper>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null); // null = all
  const [period, setPeriod] = useState('30');           // shared by chart + campaign list
  const [periodMetrics, setPeriodMetrics] = useState({}); // {campaign_id: {cost, clicks, ...}}
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [applyingIds, setApplyingIds] = useState([]);
  const [activityItems, setActivityItems] = useState([]);
  const { addNotification, updateNotification } = useNotifications();

  const hasData = stats && (stats.total_campaigns > 0 || stats.total_clicks > 0 || stats.total_impressions > 0);
  const isConnected = !!user?.google_ads_connected || !!user?.google_ads_customer_id;

  useEffect(() => {
    Promise.all([
      api.get('/ads/dashboard/'),
      api.get('/ads/campaigns/').catch(() => ({ data: [] })),
    ]).then(([dashRes, campRes]) => {
      const dashData = dashRes.data;
      const camps = (campRes.data || [])
        .filter(c => c.status !== 'draft')
        .map(c => ({
          id: c.id,
          name: c.name,
          spend: parseFloat(c.cost || 0),
          spendFormatted: parseFloat(c.cost || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 }),
          active: c.status === 'active',
          managed: c.is_managed,
          platform_account_id: c.platform_account_id || '',
          account_name: c.account_name || '',
          month: c.month || null,
          impressions: c.impressions || 0,
          clicks: c.clicks || 0,
          conversions: parseFloat(c.conversions || 0),
          ctr: c.clicks > 0 && c.impressions > 0 ? parseFloat((c.clicks / c.impressions * 100).toFixed(1)) : parseFloat(c.ctr || 0),
        }));
      dashData._campaigns = camps.filter(c => c.managed).slice(0, 10);
      setAllCampaigns(camps);
      setStats(dashData);
    }).catch(() => {
      setStats({ total_spend: 0, total_clicks: 0, total_conversions: 0, avg_ctr: 0, total_campaigns: 0, _campaigns: [] });
    }).finally(() => setLoading(false));
  }, []);

  // Filtered stats based on selected campaign
  const filteredStats = (() => {
    if (!selectedCampaign || !stats) return stats;
    const c = allCampaigns.find(camp => camp.id === selectedCampaign);
    if (!c) return stats;
    return {
      ...stats,
      currency: c.currency || stats.currency,
      mixed_currency: false,
      total_spend: c.spend,
      total_clicks: c.clicks,
      total_impressions: c.impressions,
      total_conversions: c.conversions,
      avg_ctr: c.ctr,
      total_campaigns: 1,
      active_campaigns: c.active ? 1 : 0,
      // Month-to-date for just this campaign, so the cards respect the selection.
      current_month: c.month
        ? { ...c.month, month_label: stats?.current_month?.month_label }
        : stats?.current_month,
      _campaigns: [c],
    };
  })();
  // Waluta pulpitu (dominująca waluta kont w zakresie; z backendu DashboardStatsView).
  const curCode = filteredStats?.currency || 'PLN';

  useEffect(() => {
    api.get('/ads/suggestions/')
      .then(res => setSuggestions(res.data.slice(0, 5)))
      .catch(() => setSuggestions([]));
  }, []);

  // Per-campaign metrics for the selected period, so the managed-campaigns list
  // reflects the same period as the performance chart.
  useEffect(() => {
    api.get(`/ads/campaigns/period-metrics/?period=${period}`)
      .then(res => setPeriodMetrics(res.data?.metrics || {}))
      .catch(() => setPeriodMetrics({}));
  }, [period]);

  // Per-platform breakdown shown on hover over the stat cards. Only when viewing
  // the whole account (not a single selected campaign) and there is data.
  const showBreakdown = !selectedCampaign && Array.isArray(stats?.by_platform) && stats.by_platform.length > 0;
  const fmtDate = (iso) => { if (!iso) return ''; const [y, m, d] = iso.split('-'); return `${d}.${m}.${y}`; };
  const periodText = stats?.period ? `${fmtDate(stats.period.date_from)} – ${fmtDate(stats.period.date_to)}` : t('dashboard.provenance.last30days');
  const accountLines = (stats?.by_platform || [])
    .filter(p => p.accounts && p.accounts.length)
    .map(p => `${p.label}: ${p.accounts.join(', ')}`);
  // valFn(platform, period) -> formatted string, period is 'month' | 'd30'.
  const breakdownTooltip = (valFn) => {
    if (!showBreakdown) return null;
    return (
      <div style={{ fontSize: 12, lineHeight: 1.45 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, fontSize: 10, opacity: 0.7, marginBottom: 2 }}>
          <span style={{ minWidth: 64, textAlign: 'right' }}>{t('dashboard.provenance.thisMonth')}</span>
          <span style={{ minWidth: 64, textAlign: 'right' }}>{t('dashboard.provenance.days30')}</span>
        </div>
        {stats.by_platform.map(p => (
          <div key={p.platform} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'baseline' }}>
            <span style={{ opacity: 0.85 }}>
              {p.label}<span style={{ opacity: 0.6 }}> · {t('dashboard.provenance.campaignsActive', { campaigns: p.campaigns, active: p.active_campaigns })}</span>
            </span>
            <span style={{ display: 'flex', gap: 16 }}>
              <span style={{ fontWeight: 600, minWidth: 64, textAlign: 'right' }}>{valFn(p, 'month')}</span>
              <span style={{ fontWeight: 600, minWidth: 64, textAlign: 'right', opacity: 0.85 }}>{valFn(p, 'd30')}</span>
            </span>
          </div>
        ))}
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.18)', opacity: 0.8, fontSize: 10.5 }}>
          <div><b>{t('dashboard.provenance.thisMonthLabel')}</b> {stats?.current_month?.month_label ? t('dashboard.provenance.fromFirst', { label: stats.current_month.month_label }) : t('dashboard.provenance.currentMonth')} · <b>{t('dashboard.provenance.days30Label')}</b> {periodText}</div>
          {accountLines.map(l => <div key={l}><b>{t('dashboard.provenance.accountLabel')}</b> {l}</div>)}
          <div style={{ marginTop: 2, opacity: 0.85 }}>{t('dashboard.provenance.sourceNote')}</div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    api.get('/accounts/logs/?page_size=5')
      .then(res => {
        const logs = res.data.results || res.data || [];
        setActivityItems(logs.slice(0, 5).map(log => ({
          time: new Date(log.created_at).toLocaleString('pl-PL', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }),
          text: log.description,
          campaign: log.campaign_name || '',
        })));
      })
      .catch(() => setActivityItems([]));
  }, []);

  const handleApplySuggestion = async (suggestion) => {
    setApplyingIds(prev => [...prev, suggestion.id]);
    const notifId = addNotification(t('dashboard.suggestions.applying', { title: suggestion.title }), 'loading');

    try {
      await api.post(`/ads/suggestions/${suggestion.id}/apply/`);
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      updateNotification(notifId, { message: t('dashboard.suggestions.applied', { title: suggestion.title }), type: 'success' });
    } catch {
      updateNotification(notifId, { message: t('dashboard.suggestions.error', { title: suggestion.title }), type: 'error' });
    } finally {
      setApplyingIds(prev => prev.filter(id => id !== suggestion.id));
    }
  };

  if (loading) return (
    <Flex align="center" justify="center" h={256}>
      <AnimatedLogo size={48} />
    </Flex>
  );

  return (
    <Box px="lg" py="md">
      {/* Header */}
      <StickyHeader>
        <Title order={3}>{t('dashboard.title')}</Title>
        <Group gap="sm">
          {allCampaigns.filter(c => c.managed).length > 0 && (
            <Select
              placeholder={t('dashboard.campaignSelect.placeholder')}
              data={allCampaigns.filter(c => c.managed).map(c => ({ value: c.id, label: c.name }))}
              value={selectedCampaign}
              onChange={setSelectedCampaign}
              clearable
              size="sm"
              style={{ width: 220 }}
            />
          )}
          {hasData && <ExportMenu baseUrl="/ads/dashboard/export/" filename="droid-dashboard" />}
        </Group>
      </StickyHeader>
      <SEO title="Dashboard" path="/app" noIndex />

      {/* Status banner */}
      {!hasData && (
        <Paper
          p="md"
          mb="md"
          withBorder
          style={{
            background: 'var(--mantine-color-brand-0)',
            borderColor: 'var(--mantine-color-brand-2)',
          }}
        >
          <Group justify="space-between" wrap="nowrap">
            <Box>
              {!isConnected ? (
                <>
                  <Text size="sm" fw={500} c="dark">{t('dashboard.banner.step1Title')}</Text>
                  <Text size="xs" c="dimmed" mt={2}>{t('dashboard.banner.step1Text')}<Anchor href="/poradniki/google-ads-setup" size="xs">{t('dashboard.banner.step1Link')}</Anchor></Text>
                </>
              ) : (
                <>
                  <Text size="sm" fw={500} c="dark">{t('dashboard.banner.step2Title')}</Text>
                  <Text size="xs" c="dimmed" mt={2}>{t('dashboard.banner.step2Text')}</Text>
                </>
              )}
            </Box>
            <Button component={Link} to="/app/campaigns" size="compact-sm" variant="filled" style={{ flexShrink: 0 }}>
              {!isConnected ? t('dashboard.banner.connectButton') : t('dashboard.banner.manageButton')}
            </Button>
          </Group>
        </Paper>
      )}

      {/* Stat cards: each shows the current calendar month (primary, since the
          1st) and the rolling last-30-days figure (secondary). */}
      <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md" mb="md">
        <StatCard
          label={t('dashboard.stats.spend')}
          value={formatMoney(filteredStats?.current_month?.spend || 0, curCode)}
          secondaryValue={formatMoney(filteredStats?.total_spend || 0, curCode, { decimals: 0 })}
          empty={!hasData}
          icon={<CurrencyDollar size={18} weight="duotone" color="var(--mantine-color-brand-5)" />}
          tooltip={breakdownTooltip((p, per) => formatMoney((per === 'month' ? p.month_spend : p.spend) || 0, curCode, { decimals: 0 }))}
        />
        <StatCard
          label={t('dashboard.stats.clicks')}
          value={(filteredStats?.current_month?.clicks || 0).toLocaleString('pl-PL')}
          secondaryValue={(filteredStats?.total_clicks || 0).toLocaleString('pl-PL')}
          empty={!hasData}
          icon={<CursorClick size={18} weight="duotone" color="var(--mantine-color-brand-5)" />}
          tooltip={breakdownTooltip((p, per) => ((per === 'month' ? p.month_clicks : p.clicks) || 0).toLocaleString('pl-PL'))}
        />
        <StatCard
          label={t('dashboard.stats.conversions')}
          value={Math.round(filteredStats?.current_month?.conversions || 0).toLocaleString('pl-PL')}
          secondaryValue={Math.round(parseFloat(filteredStats?.total_conversions) || 0).toLocaleString('pl-PL')}
          empty={!hasData}
          icon={<Target size={18} weight="duotone" color="var(--mantine-color-brand-5)" />}
          tooltip={breakdownTooltip((p, per) => Math.round((per === 'month' ? p.month_conversions : p.conversions) || 0).toLocaleString('pl-PL'))}
        />
        <StatCard
          label={t('dashboard.stats.ctr')}
          value={`${parseFloat(filteredStats?.current_month?.ctr || 0).toFixed(1)}%`}
          secondaryValue={`${parseFloat(filteredStats?.avg_ctr || 0).toFixed(1)}%`}
          empty={!hasData}
          icon={<Lightning size={18} weight="duotone" color="var(--mantine-color-brand-5)" />}
          tooltip={breakdownTooltip((p, per) => {
            const clk = per === 'month' ? p.month_clicks : p.clicks;
            const imp = per === 'month' ? p.month_impressions : p.impressions;
            return `${imp > 0 ? (clk / imp * 100).toFixed(1) : '0.0'}%`;
          })}
        />
      </SimpleGrid>

      {/* Always-visible data provenance: period + source. Details (per-platform,
          accounts) appear on hover over each card. */}
      {showBreakdown && hasData && (
        <Text size="xs" c="dimmed" mb="md" mt={-6}>
          {t('dashboard.provenance.summary', {
            monthParen: stats?.current_month?.month_label
              ? t('dashboard.provenance.monthParen', { label: stats.current_month.month_label })
              : '',
            period: periodText,
          })}
        </Text>
      )}

      {/* Week-over-Week comparison */}
      {stats?.wow?.has_data && (
        <Paper withBorder p="md" radius="md" mb="md">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="sm" style={{ letterSpacing: '0.05em' }}>{t('dashboard.wow.title')}</Text>
          <SimpleGrid cols={{ base: 3, md: 6 }} spacing="sm">
            {[
              { key: 'clicks', label: t('dashboard.wow.clicks'), fmt: v => v.toLocaleString('pl-PL') },
              { key: 'impressions', label: t('dashboard.wow.impressions'), fmt: v => v.toLocaleString('pl-PL') },
              { key: 'cost', label: t('dashboard.wow.cost'), fmt: v => formatMoney(v, curCode) },
              { key: 'conversions', label: t('dashboard.wow.conversions'), fmt: v => v },
              { key: 'cpc', label: t('dashboard.wow.cpc'), fmt: v => formatMoney(v, curCode) },
              { key: 'ctr', label: t('dashboard.wow.ctr'), fmt: v => `${v.toFixed(1)}%` },
            ].map(m => {
              const current = stats.wow.this_week[m.key];
              const change = stats.wow.changes[m.key];
              const isGood = m.key === 'cost' || m.key === 'cpc' ? change < 0 : change > 0;
              const isNeutral = change === 0;
              const noComparison = change === null;
              return (
                <div key={m.key} className="text-center">
                  <Text size="xs" c="dimmed" mb={2}>{m.label}</Text>
                  <Text size="sm" fw={600} ff="monospace">{m.fmt(current)}</Text>
                  {noComparison ? (
                    <Text size="xs" c="gray.4" mt={2}>{t('dashboard.wow.new')}</Text>
                  ) : !isNeutral ? (
                    <Text size="xs" c={isGood ? 'green.6' : 'red.6'} fw={500} mt={2}>
                      {change > 0 ? <TrendUp size={10} weight="bold" style={{ display: 'inline', marginRight: 2 }} /> : <TrendDown size={10} weight="bold" style={{ display: 'inline', marginRight: 2 }} />}
                      {change > 0 ? '+' : ''}{change}%
                    </Text>
                  ) : (
                    <Text size="xs" c="gray.4" mt={2}>0%</Text>
                  )}
                </div>
              );
            })}
          </SimpleGrid>
        </Paper>
      )}

      {/* Chart */}
      <Box mb="md">
        <PerformanceChart campaignId={selectedCampaign} isConnected={isConnected} period={period} setPeriod={setPeriod} currency={curCode} />
      </Box>

      {/* GA4 Analytics */}
      {user?.organization?.ga4_property_id && (
        <GA4Widget />
      )}

      {/* Campaigns */}
      <Box mb="md">
        <CampaignsList campaigns={hasData ? (filteredStats._campaigns || []) : []} selectedAccount={stats?.account_scope?.google} periodMetrics={periodMetrics} period={period} />
      </Box>

      {/* Bottom section */}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <SuggestionsList
          suggestions={selectedCampaign ? suggestions.filter(s => s.campaign === selectedCampaign || s.campaign_name === allCampaigns.find(c => c.id === selectedCampaign)?.name) : suggestions}
          onApply={handleApplySuggestion}
          applyingIds={applyingIds}
        />
        <ActivityTimeline items={selectedCampaign ? activityItems.filter(a => a.campaign === allCampaigns.find(c => c.id === selectedCampaign)?.name) : activityItems} />
      </SimpleGrid>
    </Box>
  );
}
